
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Studio } from '@/hooks/use-studios';
import { useToast } from '@/components/ui/use-toast';
import { LocateFixed, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

// Set Mapbox access token - using a current working token
mapboxgl.accessToken = 'pk.eyJ1Ijoic2FpbnQ3ZGlvIiwiYSI6ImNtYTMyb2cwdzE3M2syanNmNmZuOTJqaW8ifQ.BgLhJopYPQZNs4b-9J1fzg';

interface StudioMapProps {
  studios: Studio[];
  selectedStudio: Studio | null;
  onStudioSelect: (studio: Studio) => void;
}

const StudioMap: React.FC<StudioMapProps> = ({ 
  studios, 
  selectedStudio,
  onStudioSelect 
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState<{longitude: number, latitude: number} | null>(null);
  const [locationRequestInProgress, setLocationRequestInProgress] = useState(false);
  const { toast } = useToast();

  // Function to get user's location with improved handling
  const getUserLocation = () => {
    if (!map.current || locationRequestInProgress) return;
    
    setLocationRequestInProgress(true);
    
    if (navigator.geolocation) {
      // Zeige Toast für besseres Feedback während des Ladevorgangs
      toast({
        title: "Standort wird ermittelt",
        description: "Bitte erlaube den Zugriff auf deinen Standort.",
        duration: 3000,
      });
      
      navigator.geolocation.getCurrentPosition(
        position => {
          const { longitude, latitude } = position.coords;
          setUserLocation({ longitude, latitude });
          setLocationRequestInProgress(false);
          
          // Fly to user location
          map.current?.flyTo({
            center: [longitude, latitude],
            zoom: 13,
            speed: 1.8,
            curve: 1
          });

          // Add user marker
          const el = document.createElement('div');
          el.className = 'user-location-marker';
          el.innerHTML = `<div class="w-4 h-4 bg-blue-500 rounded-full shadow-lg shadow-blue-500/30 ring-4 ring-blue-500/20"></div>`;
          
          try {
            if (map.current) {
              new mapboxgl.Marker(el)
                .setLngLat([longitude, latitude])
                .setPopup(new mapboxgl.Popup().setHTML('<div class="p-2"><div class="text-sm font-medium">Dein Standort</div></div>'))
                .addTo(map.current);
            }
          } catch (error) {
            console.error("Error adding user location marker:", error);
          }
            
          toast({
            title: "Standort gefunden",
            description: "Karte wurde auf deinen Standort zentriert.",
            duration: 2000,
            variant: "default",
          });
        }, 
        error => {
          console.info("Geolocation error:", error.message, error.code);
          setLocationRequestInProgress(false);
          
          let errorMessage = "Dein Standort konnte nicht ermittelt werden.";
          
          // Detailliertere Fehlermeldungen basierend auf dem Fehlercode
          switch(error.code) {
            case 1: // PERMISSION_DENIED
              errorMessage = "Zugriff auf den Standort wurde verweigert. Bitte erlaube den Zugriff in deinen Browsereinstellungen.";
              break;
            case 2: // POSITION_UNAVAILABLE
              errorMessage = "Dein Standort ist derzeit nicht verfügbar. Verwende die Kartensteuerung zum Navigieren.";
              break;
            case 3: // TIMEOUT
              errorMessage = "Zeitüberschreitung bei der Standortermittlung. Versuche es erneut oder suche manuell.";
              break;
          }
          
          toast({
            title: "Standortermittlung fehlgeschlagen",
            description: errorMessage,
            variant: "destructive",
            duration: 5000,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,          // Erhöhter Timeout
          maximumAge: 60000        // Cache für 1 Minute erlaubt
        }
      );
    } else {
      setLocationRequestInProgress(false);
      toast({
        title: "Standortermittlung nicht verfügbar",
        description: "Dein Browser unterstützt keine Standortermittlung.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    
    try {
      console.log("Initializing map...");
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [10.4515, 51.1657], // Center of Germany
        zoom: 5
      });
      
      // Wait for the map to load before adding controls and markers
      map.current.on('load', () => {
        console.log("Map loaded, adding controls");
        try {
          // Add navigation controls
          map.current?.addControl(new mapboxgl.NavigationControl(), 'top-right');
          
          // Add fullscreen control
          map.current?.addControl(new mapboxgl.FullscreenControl());
          
          setMapLoaded(true);
        } catch (error) {
          console.error("Error adding map controls:", error);
        }
      });
    } catch (error) {
      console.error("Error initializing map:", error);
      toast({
        title: "Fehler beim Laden der Karte",
        description: "Die Karte konnte nicht geladen werden. Bitte versuche es später erneut.",
        variant: "destructive",
        duration: 5000,
      });
    }
    
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);
  
  // Add markers when studios data changes or map loads
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      console.log("Map not ready yet, skipping marker creation");
      return;
    }
    
    console.log("Map is ready, processing studios...");
    
    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
    
    // If no studios, add fallback markers
    if (!studios || studios.length === 0) {
      console.log('No studios provided, adding fallback markers');
      addFallbackMarkers();
      return;
    }
    
    // Log the number of studios and how many have geo_location
    const studiosWithLocation = studios.filter(studio => studio.geo_location);
    console.log(`Adding markers for ${studios.length} studios (${studiosWithLocation.length} with location)`);
    
    // Add markers for each studio with geo_location
    studios.forEach(studio => {
      if (studio.geo_location) {
        const { longitude, latitude } = studio.geo_location;
        
        // Skip invalid coordinates
        if (isNaN(longitude) || isNaN(latitude) || (longitude === 0 && latitude === 0)) {
          console.warn('Invalid geo_location for studio:', studio.name);
          return;
        }
        
        try {
          // Create marker element
          const el = document.createElement('div');
          el.className = 'studio-marker';
          el.innerHTML = `<div class="w-4 h-4 ${studio.is_registered ? 'bg-chrona-accent' : 'bg-gray-400'} rounded-full shadow-lg ${studio.is_registered ? 'shadow-chrona-accent/30' : 'shadow-gray-500/30'} pulse"></div>`;
          
          // Create popup
          const popup = new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div class="p-2">
                <div class="text-sm font-medium">${studio.name}</div>
                <div class="text-xs text-gray-500 mt-1">${studio.description || ''}</div>
                <button id="select-studio-${studio.id}" class="mt-2 px-3 py-1 text-xs bg-chrona-accent text-white rounded-full hover:bg-chrona-accent-light transition-colors">Auswählen</button>
              </div>
            `);
          
          // Add marker to map
          const marker = new mapboxgl.Marker(el)
            .setLngLat([longitude, latitude])
            .setPopup(popup)
            .addTo(map.current!);
          
          // Add event listener for select button in popup
          popup.on('open', () => {
            setTimeout(() => {
              const selectBtn = document.getElementById(`select-studio-${studio.id}`);
              if (selectBtn) {
                selectBtn.addEventListener('click', () => {
                  onStudioSelect(studio);
                  marker.togglePopup(); // Close popup after selection
                });
              }
            }, 10);
          });
          
          // Store marker for later removal
          markers.current.push(marker);
        } catch (error) {
          console.error("Error adding marker for studio:", studio.name, error);
        }
      }
    });
    
    // If no valid studios with geo_location, add fallback markers
    if (markers.current.length === 0) {
      console.log('No valid studio markers added, using fallbacks');
      addFallbackMarkers();
    } else if (markers.current.length > 0) {
      // Fit map to bounds of all markers
      try {
        const bounds = new mapboxgl.LngLatBounds();
        markers.current.forEach(marker => {
          bounds.extend(marker.getLngLat());
        });
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 12,
          duration: 1000
        });
      } catch (error) {
        console.error("Error fitting bounds:", error);
      }
    }
  }, [studios, mapLoaded, onStudioSelect]);
  
  // Update selected studio marker
  useEffect(() => {
    if (!map.current || !mapLoaded || !selectedStudio || !selectedStudio.geo_location) return;
    
    const { longitude, latitude } = selectedStudio.geo_location;
    
    try {
      // Find marker for selected studio
      const marker = markers.current.find(m => {
        const lngLat = m.getLngLat();
        return Math.abs(lngLat.lng - longitude) < 0.001 && Math.abs(lngLat.lat - latitude) < 0.001;
      });
      
      if (marker) {
        // Highlight marker
        const el = marker.getElement();
        el.classList.add('selected');
        
        // Pan to marker
        map.current.flyTo({
          center: [longitude, latitude],
          zoom: 14,
          duration: 1000
        });
      }
    } catch (error) {
      console.error("Error highlighting selected studio:", error);
    }
  }, [selectedStudio, mapLoaded]);
  
  // Fallback markers function
  const addFallbackMarkers = () => {
    if (!map.current) return;
    
    const cities = [
      { lng: 13.404954, lat: 52.520008, name: "Berlin Studios", desc: "Professionelle Beauty-Studios in Berlin" },
      { lng: 11.581981, lat: 48.135124, name: "München Beauty", desc: "Premium Wellness & Beauty" },
      { lng: 9.993682, lat: 53.551086, name: "Hamburg Salon", desc: "Maritimes Ambiente mit Top-Service" },
      { lng: 8.682127, lat: 50.110924, name: "Frankfurt Spa", desc: "Business & Wellness im Herzen der Stadt" },
      { lng: 6.959974, lat: 50.938361, name: "Köln Wellness", desc: "Rundum-Wohlfühlatmosphäre" },
      { lng: 13.738144, lat: 51.050407, name: "Dresden Massage", desc: "Entspannung in historischem Ambiente" },
    ];
    
    try {
      cities.forEach(city => {
        // Create a mock studio object
        const mockStudio: Studio = {
          id: `mock-${city.name}`,
          name: city.name,
          description: city.desc,
          is_registered: true,
          geo_location: {
            longitude: city.lng,
            latitude: city.lat
          },
          rating_avg: 4.5,  // Default mock rating
          rating_count: 10  // Default mock rating count
        };
        
        // Create marker element
        const el = document.createElement('div');
        el.className = 'studio-marker';
        el.innerHTML = `<div class="w-4 h-4 bg-chrona-accent rounded-full shadow-lg shadow-chrona-accent/30 pulse"></div>`;
        
        // Create popup
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(`
            <div class="p-2">
              <div class="text-sm font-medium">${city.name}</div>
              <div class="text-xs text-gray-500 mt-1">${city.desc}</div>
              <button id="select-studio-${mockStudio.id}" class="mt-2 px-3 py-1 text-xs bg-chrona-accent text-white rounded-full hover:bg-chrona-accent-light transition-colors">Auswählen</button>
            </div>
          `);
        
        // Add marker to map
        const marker = new mapboxgl.Marker(el)
          .setLngLat([city.lng, city.lat])
          .setPopup(popup)
          .addTo(map.current!);
        
        // Add event listener for select button in popup
        popup.on('open', () => {
          setTimeout(() => {
            const selectBtn = document.getElementById(`select-studio-${mockStudio.id}`);
            if (selectBtn) {
              selectBtn.addEventListener('click', () => {
                onStudioSelect(mockStudio);
                marker.togglePopup(); // Close popup after selection
              });
            }
          }, 10);
        });
        
        // Store marker for later removal
        markers.current.push(marker);
      });
      
      // Fit map to bounds of all markers
      const bounds = new mapboxgl.LngLatBounds();
      markers.current.forEach(marker => {
        bounds.extend(marker.getLngLat());
      });
      map.current?.fitBounds(bounds, {
        padding: 50,
        maxZoom: 12,
        duration: 1000
      });
    } catch (error) {
      console.error("Error adding fallback markers:", error);
    }
  };
  
  const handleZoomIn = () => {
    if (!map.current) return;
    try {
      map.current.zoomIn();
    } catch (error) {
      console.error("Error zooming in:", error);
    }
  };
  
  const handleZoomOut = () => {
    if (!map.current) return;
    try {
      map.current.zoomOut();
    } catch (error) {
      console.error("Error zooming out:", error);
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative w-full h-[400px] mb-8 rounded-xl overflow-hidden shadow-xl"
    >
      <div ref={mapContainer} className="absolute inset-0" />
      
      {/* Controls overlay */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        <Button 
          onClick={getUserLocation}
          size="icon"
          variant="secondary"
          className="w-10 h-10 rounded-full bg-black/60 text-white border border-white/10 hover:bg-chrona-accent/80 transition-colors backdrop-blur-sm shadow-lg"
        >
          <LocateFixed size={18} />
        </Button>
      </div>
      
      <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
        <Button 
          onClick={handleZoomIn}
          size="icon"
          variant="secondary"
          className="w-9 h-9 rounded-full bg-black/60 text-white border border-white/10 hover:bg-chrona-accent/80 transition-colors backdrop-blur-sm shadow-lg"
        >
          <Plus size={18} />
        </Button>
        <Button 
          onClick={handleZoomOut}
          size="icon"
          variant="secondary"
          className="w-9 h-9 rounded-full bg-black/60 text-white border border-white/10 hover:bg-chrona-accent/80 transition-colors backdrop-blur-sm shadow-lg"
        >
          <Minus size={18} />
        </Button>
      </div>
      
      {/* Custom styling for map markers and popups */}
      <style dangerouslySetInnerHTML={{ __html: `
        .mapboxgl-popup-content {
          background-color: rgba(32, 32, 32, 0.9);
          color: white;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
          padding: 12px;
        }
        .mapboxgl-popup-tip {
          border-top-color: rgba(32, 32, 32, 0.9) !important;
          border-bottom-color: rgba(32, 32, 32, 0.9) !important;
        }
        .studio-marker {
          cursor: pointer;
        }
        .studio-marker.selected > div {
          transform: scale(1.5);
          box-shadow: 0 0 0 4px rgba(44, 110, 73, 0.3);
        }
        .user-location-marker {
          z-index: 2;
        }
        .pulse {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(44, 110, 73, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(44, 110, 73, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(44, 110, 73, 0);
          }
        }
      `}} />
    </motion.div>
  );
};

export default StudioMap;
